import { useEffect, useRef, useState } from 'preact/hooks';
import { api, baixarBlob, mensagemDoErro } from '../api';
import { ROTULO_STATUS, dataCurta } from '../format';
import { useMunicipios } from '../lookups';
import { pode } from '../permissoes';
import { usuario } from '../state';
import type { Acao, ArquivoAcao, HistoricoItem, StatusAcao } from '../types';
import { Barra, Campo, Carregando, ErroForm, Modal, avisar, confirmar } from '../ui';
import { ChipStatus, STATUS_OPCOES, dataHora, tamanhoLegivel } from './comum';
import { CampoData } from '../componentes/Campos';

const MAX_BYTES = 15 * 1024 * 1024;
const ROTULO_ORIGEM = { MANUAL: 'cadastro manual', CSV: 'importação por CSV', INTEGRACAO: 'integração' } as const;

type Props = { id: string; onFechar: () => void; onMudou: () => void };

export function AcaoDetalheModal({ id, onFechar, onMudou }: Props) {
  const podeEscrever = pode(usuario.value?.perfil, 'acao:write');
  const { lista: municipios } = useMunicipios();

  const [acao, setAcao] = useState<Acao | null>(null);
  const [historico, setHistorico] = useState<HistoricoItem[]>([]);
  const [arquivos, setArquivos] = useState<ArquivoAcao[]>([]);
  const [erroCarga, setErroCarga] = useState('');

  async function carregar() {
    try {
      const [a, h, f] = await Promise.all([
        api<Acao>(`/acoes/${id}`),
        api<HistoricoItem[]>(`/acoes/${id}/historico`),
        api<ArquivoAcao[]>(`/acoes/${id}/arquivos`),
      ]);
      setAcao(a);
      setHistorico(h);
      setArquivos(f);
    } catch (e) {
      setErroCarga(mensagemDoErro(e));
    }
  }
  useEffect(() => {
    void carregar();
  }, [id]);

  const mudou = async () => {
    await carregar();
    onMudou();
  };

  return (
    <Modal titulo={acao?.titulo ?? 'Ação'} onFechar={onFechar} largo>
      {erroCarga && <ErroForm texto={erroCarga} />}
      {!acao && !erroCarga && <Carregando />}
      {acao && (
        <div class="detalhe-acao">
          <section class="resumo-acao">
            <div class="chips"><ChipStatus status={acao.status} atrasada={acao.atrasada} /></div>
            <p class="dica">
              {acao.municipio} · {acao.secretaria} · {acao.projeto}
            </p>
            <div class="prog-celula grande"><Barra pct={acao.progressoPct} /><strong>{acao.progressoPct}%</strong></div>
            <p class="dica">
              Prazo: {dataCurta(acao.dataFimPrevista, true)}
              {acao.dataFimReal && ` · concluída em ${dataCurta(acao.dataFimReal, true)}`}
              {acao.responsavel && ` · responsável: ${acao.responsavel}`} · {ROTULO_ORIGEM[acao.origem]}
            </p>
            {acao.descricao && <p class="descricao">{acao.descricao}</p>}
          </section>

          {podeEscrever && <RegistrarAndamento acao={acao} onSalvo={mudou} />}

          <section class="bloco">
            <h3>Linha do tempo</h3>
            <ol class="timeline">
              {[...historico].reverse().map((h) => (
                <li key={h.id}>
                  <div class="timeline-topo">
                    <strong>{ROTULO_STATUS[h.status]} · {h.progressoPct}%</strong>
                    <small>{dataHora(h.registradoEm)}{h.autor ? ` · ${h.autor}` : ''}</small>
                  </div>
                  {h.nota && <p>{h.nota}</p>}
                </li>
              ))}
            </ol>
          </section>

          <Arquivos acaoId={id} arquivos={arquivos} podeEscrever={podeEscrever} onMudou={mudou} />

          {podeEscrever && (
            <details class="bloco editar">
              <summary><h3>Editar dados da ação</h3></summary>
              <EditarDados acao={acao} municipios={municipios} onSalvo={mudou} />
            </details>
          )}
        </div>
      )}
    </Modal>
  );
}

function RegistrarAndamento({ acao, onSalvo }: { acao: Acao; onSalvo: () => Promise<void> }) {
  const [status, setStatus] = useState<StatusAcao>(acao.status);
  const [pct, setPct] = useState(String(acao.progressoPct));
  const [nota, setNota] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Quando a ação muda por fora (depois de salvar), o formulário acompanha.
  useEffect(() => {
    setStatus(acao.status);
    setPct(String(acao.progressoPct));
  }, [acao.status, acao.progressoPct]);

  const n = Number(pct);
  const avisoConclusao = pct !== '' && n === 100 && status !== 'CONCLUIDA' && (status === 'PLANEJADA' || status === 'EM_ANDAMENTO');

  function aoMudarStatus(s: StatusAcao) {
    setStatus(s);
    if (s === 'CONCLUIDA') setPct('100'); // concluída exige 100%
  }

  async function salvar(e: Event) {
    e.preventDefault();
    setErro('');
    const body: Record<string, unknown> = {};
    if (status !== acao.status) body.status = status;
    if (pct !== '' && n !== acao.progressoPct) body.progressoPct = n;
    if (nota.trim()) body.nota = nota.trim();
    if (Object.keys(body).length === 0) return setErro('Altere a situação, o progresso ou escreva uma nota.');
    if (pct !== '' && (!Number.isInteger(n) || n < 0 || n > 100)) return setErro('O progresso deve ser um número inteiro de 0 a 100.');

    setSalvando(true);
    try {
      await api(`/acoes/${acao.id}/atualizar`, { method: 'POST', body });
      setNota('');
      avisar('Andamento registrado.');
      await onSalvo();
    } catch (err) {
      setErro(mensagemDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section class="bloco">
      <h3>Registrar andamento</h3>
      <form class="form" onSubmit={salvar}>
        <div class="linha-form">
          <Campo rotulo="Situação" larg="meio">
            {(id) => (
              <select id={id} value={status} onChange={(e) => aoMudarStatus(e.currentTarget.value as StatusAcao)}>
                {STATUS_OPCOES.map((s) => <option key={s} value={s}>{ROTULO_STATUS[s]}</option>)}
              </select>
            )}
          </Campo>
          <Campo rotulo="Progresso (%)" larg="meio" dica={avisoConclusao ? 'Com 100%, a ação será marcada como concluída.' : undefined}>
            {(id) => <input id={id} type="number" min={0} max={100} step={1} value={pct} onInput={(e) => setPct(e.currentTarget.value)} />}
          </Campo>
        </div>
        <input type="range" min={0} max={100} value={Number.isFinite(n) ? n : 0} onInput={(e) => setPct(e.currentTarget.value)} aria-label="Ajustar progresso" class="range" />
        <Campo rotulo="Nota (opcional)" dica="Fica registrada na linha do tempo, com seu nome e a data.">
          {(id) => <textarea id={id} rows={2} maxLength={1000} value={nota} onInput={(e) => setNota(e.currentTarget.value)} />}
        </Campo>
        <ErroForm texto={erro} />
        <div class="modal-acoes"><button type="submit" class="botao primario" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar andamento'}</button></div>
      </form>
    </section>
  );
}

function EditarDados({ acao, municipios, onSalvo }: { acao: Acao; municipios: { cod: string; nome: string }[]; onSalvo: () => Promise<void> }) {
  const [titulo, setTitulo] = useState(acao.titulo);
  const [descricao, setDescricao] = useState(acao.descricao ?? '');
  const [codIbge, setCodIbge] = useState(acao.codIbge);
  const [responsavel, setResponsavel] = useState(acao.responsavel ?? '');
  const [inicio, setInicio] = useState(acao.dataInicio ?? '');
  const [fim, setFim] = useState(acao.dataFimPrevista ?? '');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function salvar(e: Event) {
    e.preventDefault();
    setErro('');
    if (titulo.trim().length < 3) return setErro('O título precisa ter ao menos 3 caracteres.');
    setSalvando(true);
    try {
      await api(`/acoes/${acao.id}`, {
        method: 'PATCH',
        body: {
          titulo: titulo.trim(),
          descricao: descricao.trim() || null,
          codIbge,
          responsavel: responsavel.trim() || null,
          dataInicio: inicio || null,
          dataFimPrevista: fim || null,
        },
      });
      avisar('Dados atualizados.');
      await onSalvo();
    } catch (err) {
      setErro(mensagemDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form class="form" onSubmit={salvar}>
      <Campo rotulo="Título">{(id) => <input id={id} required minLength={3} maxLength={200} value={titulo} onInput={(e) => setTitulo(e.currentTarget.value)} />}</Campo>
      <Campo rotulo="Descrição">{(id) => <textarea id={id} rows={3} maxLength={5000} value={descricao} onInput={(e) => setDescricao(e.currentTarget.value)} />}</Campo>
      <Campo rotulo="Município">
        {(id) => (
          <select id={id} value={codIbge} onChange={(e) => setCodIbge(e.currentTarget.value)}>
            {municipios.map((m) => <option key={m.cod} value={m.cod}>{m.nome}</option>)}
          </select>
        )}
      </Campo>
      <div class="linha-form">
        <Campo rotulo="Início" larg="meio">{(id) => <CampoData id={id} value={inicio} onChange={setInicio} />}</Campo>
        <Campo rotulo="Prazo final" larg="meio">{(id) => <CampoData id={id} value={fim} onChange={setFim} />}</Campo>
      </div>
      <Campo rotulo="Responsável">{(id) => <input id={id} maxLength={150} value={responsavel} onInput={(e) => setResponsavel(e.currentTarget.value)} />}</Campo>
      <ErroForm texto={erro} />
      <div class="modal-acoes"><button type="submit" class="botao" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar dados'}</button></div>
    </form>
  );
}

function Arquivos({ acaoId, arquivos, podeEscrever, onMudou }: { acaoId: string; arquivos: ArquivoAcao[]; podeEscrever: boolean; onMudou: () => Promise<void> }) {
  const entrada = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  async function enviar(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const arq = input.files?.[0];
    if (!arq) return;
    setErro('');
    if (arq.size > MAX_BYTES) {
      input.value = '';
      return setErro('O arquivo passa de 15 MB.');
    }
    const form = new FormData();
    form.append('file', arq);
    setEnviando(true);
    try {
      await api(`/acoes/${acaoId}/arquivos`, { method: 'POST', form });
      avisar('Arquivo enviado.');
      await onMudou();
    } catch (err) {
      setErro(mensagemDoErro(err));
    } finally {
      setEnviando(false);
      input.value = '';
    }
  }

  async function abrir(a: ArquivoAcao) {
    try {
      const blob = await baixarBlob(`/arquivos/${a.id}`);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      avisar(mensagemDoErro(err), 'erro');
    }
  }

  async function excluir(a: ArquivoAcao) {
    if (!(await confirmar(`Excluir o arquivo "${a.nomeOriginal}"? Não dá para desfazer.`, { confirmar: 'Excluir', perigo: true }))) return;
    try {
      await api(`/arquivos/${a.id}`, { method: 'DELETE' });
      avisar('Arquivo excluído.');
      await onMudou();
    } catch (err) {
      avisar(mensagemDoErro(err), 'erro');
    }
  }

  return (
    <section class="bloco">
      <h3>Fotos e documentos</h3>
      {arquivos.length === 0 && <p class="vazio">Nenhum arquivo anexado.</p>}
      <ul class="arquivos">
        {arquivos.map((a) => (
          <li key={a.id}>
            <span class="arquivo-nome">{a.nomeOriginal}<small> · {tamanhoLegivel(a.tamanho)} · {dataHora(a.criadoEm)}</small></span>
            <span class="arquivo-acoes">
              <button type="button" class="link" onClick={() => void abrir(a)}>Abrir</button>
              {podeEscrever && <button type="button" class="link perigo-txt" onClick={() => void excluir(a)}>Excluir</button>}
            </span>
          </li>
        ))}
      </ul>
      {podeEscrever && (
        <div class="upload">
          <input ref={entrada} id={`up-${acaoId}`} aria-label="Anexar foto ou documento" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf" onChange={enviar} disabled={enviando} />
          <small class="dica">{enviando ? 'Enviando…' : 'JPG, PNG, WebP ou PDF, até 15 MB (máx. 20 por ação).'}</small>
        </div>
      )}
      <ErroForm texto={erro} />
    </section>
  );
}
